/** SUNTRONIX ONE — Preview Mode banner + device viewport frame.
 *
 * Persistent top banner that appears when a Super Admin is impersonating
 * a customer.  Provides:
 *   - Tenant identity (name + ABM chip)
 *   - Role switcher
 *   - Device viewport selector
 *   - Customer switcher
 *   - Exit preview button
 *
 * Renders as a fixed top strip pushing the app shell down.  Device presets
 * clip the main content into an iframe-style viewport for responsive QA.
 */
import { useEffect, useState } from "react";
import { usePreview } from "@/context/PreviewContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { X, ChevronDown, Eye, MonitorSmartphone, UserCog, Building2 } from "lucide-react";
import api from "@/lib/api";

export default function PreviewBanner() {
  const { session, active, exit, switchRole, switchCustomer, switchDevice,
             tenants, loadTenants, ROLE_LABEL, DEVICE_PRESETS } = usePreview();
  const [abmId, setAbmId] = useState(null);

  useEffect(() => {
    if (!active) return;
    loadTenants();
  }, [active, loadTenants]);

  // Fetch ABM id from customer header for the identity chip
  useEffect(() => {
    if (!active || !session?.master_company_id) return;
    api.get(`/master-clients/${session.master_company_id}/header`)
      .then(({ data }) => setAbmId(data.abm_id))
      .catch(() => setAbmId(null));
  }, [active, session?.master_company_id]);

  if (!active) return null;

  const role = session.preview_role;
  const device = session.device || "desktop";
  const preAct = !!session.pre_activation;

  return (
    <div
      className={`sticky top-0 z-50 border-b ${
        preAct ? "bg-amber-500 border-amber-700"
                : "bg-yellow-400 border-yellow-600"} text-slate-950`}
      data-testid="preview-banner"
    >
      <div className="flex items-center flex-wrap gap-2 md:gap-3 px-3 py-1.5 text-[12px]">
        <div className="flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[11px]">
          <Eye className="h-3.5 w-3.5" strokeWidth={2} />
          {preAct ? "Pre-Activation Preview" : "Preview Mode"}
        </div>
        <span className="text-slate-800">·</span>
        <div data-testid="preview-tenant-identity" className="font-medium">
          Viewing as {session.tenant_name || "…"}
          {abmId && (
            <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-900/10 text-slate-900 text-[10px] font-mono">
              ABM {abmId}
            </span>
          )}
        </div>

        {/* Role switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="ml-auto flex items-center gap-1 rounded border border-slate-900/30 bg-white/70 hover:bg-white px-2 py-0.5"
            data-testid="preview-role-switcher"
          >
            <UserCog className="h-3.5 w-3.5" strokeWidth={1.8} />
            {ROLE_LABEL[role] || role}
            <ChevronDown className="h-3 w-3" strokeWidth={1.8} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Preview role</DropdownMenuLabel>
            {Object.entries(ROLE_LABEL).map(([k, v]) => (
              <DropdownMenuItem key={k} onClick={() => switchRole(k)}
                data-testid={`preview-role-${k}`}>
                {v} {k === role && "✓"}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Device switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex items-center gap-1 rounded border border-slate-900/30 bg-white/70 hover:bg-white px-2 py-0.5"
            data-testid="preview-device-switcher"
          >
            <MonitorSmartphone className="h-3.5 w-3.5" strokeWidth={1.8} />
            {DEVICE_PRESETS[device]?.label || device}
            <ChevronDown className="h-3 w-3" strokeWidth={1.8} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Device viewport</DropdownMenuLabel>
            {Object.entries(DEVICE_PRESETS).map(([k, v]) => (
              <DropdownMenuItem key={k} onClick={() => switchDevice(k)}
                data-testid={`preview-device-${k}`}>
                {v.label} {k === device && "✓"}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Customer switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex items-center gap-1 rounded border border-slate-900/30 bg-white/70 hover:bg-white px-2 py-0.5"
            data-testid="preview-customer-switcher"
          >
            <Building2 className="h-3.5 w-3.5" strokeWidth={1.8} />
            Switch customer
            <ChevronDown className="h-3 w-3" strokeWidth={1.8} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto w-64">
            <DropdownMenuLabel>Customers</DropdownMenuLabel>
            {tenants.customers?.map((t) => (
              <DropdownMenuItem key={t.id} onClick={() => switchCustomer(t.id)}
                data-testid={`preview-tenant-${t.id}`}>
                <div>
                  <div className="font-medium text-[12px]">{t.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {t.code}{t.country ? ` · ${t.country}` : ""}
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
            {tenants.master_clients?.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Master clients</DropdownMenuLabel>
                {tenants.master_clients.map((t) => (
                  <DropdownMenuItem key={t.id} onClick={() => switchCustomer(t.id)}
                    data-testid={`preview-mcl-${t.id}`}>
                    {t.name}
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          onClick={exit}
          className="flex items-center gap-1 rounded bg-slate-900 text-yellow-200 hover:bg-slate-800 px-2.5 py-0.5 font-semibold uppercase text-[11px]"
          data-testid="preview-exit-btn"
        >
          <X className="h-3 w-3" strokeWidth={2.2} />
          Exit preview
        </button>
      </div>
      <div className="px-3 pb-1 text-[10px] text-slate-800/80">
        No production changes will be saved · Writes return
        <code className="mx-1 font-mono bg-black/10 px-1 rounded">preview_mode_blocked</code>
      </div>
    </div>
  );
}


/** Wraps the shell content in a device viewport frame when preview device
 *  is not "desktop". */
export function PreviewDeviceFrame({ children }) {
  const { session, active, DEVICE_PRESETS } = usePreview();
  if (!active) return children;
  const preset = DEVICE_PRESETS[session?.device];
  if (!preset || !preset.width) return children;
  return (
    <div className="w-full min-h-full flex justify-center bg-slate-100 py-6"
      data-testid="preview-device-frame">
      <div
        className="bg-white border border-slate-300 shadow-lg overflow-auto rounded"
        style={{ width: preset.width, maxHeight: `${preset.height}px`,
                    minHeight: `${Math.min(preset.height, 700)}px` }}
      >
        {children}
      </div>
    </div>
  );
}
